import { useState } from 'react';
import { Bot, Activity, Globe2, TrendingUp } from 'lucide-react';
import { Card, LoadingSpinner, ErrorMessage } from '../components';
import { useBotActivity } from '../hooks';
import { formatDistanceToNow } from 'date-fns';

const SITES = [
  { value: '', label: 'All Products' },
  { value: 'konvertibly', label: 'Konvertibly' },
  { value: 'roveera', label: 'Roveera' },
  { value: 'stellrai', label: 'Stellrai' },
  { value: 'twiralo-sales', label: 'Twiralo Sales' },
  { value: 'getcalmindex', label: 'GetCalmIndex' },
];

export function BotActivity() {
  const [site, setSite] = useState('');
  const [days, setDays] = useState(30);
  const { summary, daily, loading, error } = useBotActivity(site || undefined, days);

  if (loading && summary.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  const totalHits = summary.reduce((sum: number, s: any) => sum + s.hitCount, 0);
  const distinctBots = new Set(summary.map((s: any) => s.botName)).size;
  const distinctSites = new Set(summary.map((s: any) => s.site)).size;
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayTotal = daily
    .filter((d: any) => String(d.day).slice(0, 10) === todayStr)
    .reduce((sum: number, d: any) => sum + d.hitCount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">AI Bot Activity</h1>
          <p className="text-dark-400 mt-1">
            AI crawlers and search bots detected at the Cloudflare Worker layer, across all products
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            {SITES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-purple-500/20">
              <Bot className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <div className="text-sm text-dark-400">Bot Hits ({days}d)</div>
              <div className="text-2xl font-bold text-white">{totalHits.toLocaleString()}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Activity className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div className="text-sm text-dark-400">Hits Today</div>
              <div className="text-2xl font-bold text-white">{todayTotal.toLocaleString()}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/20">
              <TrendingUp className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <div className="text-sm text-dark-400">Distinct Bots</div>
              <div className="text-2xl font-bold text-white">{distinctBots}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-orange-500/20">
              <Globe2 className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <div className="text-sm text-dark-400">Products Reporting</div>
              <div className="text-2xl font-bold text-white">{distinctSites}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Breakdown table */}
      <Card title="Bot Breakdown" subtitle={`${summary.length} bot/product combinations, last ${days} days`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-dark-400 border-b border-dark-700">
                <th className="py-2 pr-4">Bot</th>
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">Hits</th>
                <th className="py-2 pr-4">Last Seen</th>
                <th className="py-2">Sample Paths</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((s: any, i: number) => (
                <tr key={i} className="border-b border-dark-800">
                  <td className="py-2 pr-4 text-white font-medium">{s.botName}</td>
                  <td className="py-2 pr-4 text-dark-300">{s.site}</td>
                  <td className="py-2 pr-4 text-white">{s.hitCount.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-dark-400">
                    {s.lastSeen ? formatDistanceToNow(new Date(s.lastSeen), { addSuffix: true }) : '—'}
                  </td>
                  <td className="py-2 text-dark-400 max-w-md truncate">
                    {(s.samplePaths || []).slice(0, 3).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {summary.length === 0 && (
            <div className="text-center py-12">
              <Bot className="h-12 w-12 text-dark-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No bot activity yet</h3>
              <p className="text-dark-400">Once a product's Worker detects a bot, it will show up here.</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
